import {
  calculateConversionRate,
  calculateTextingRatio,
} from '@common/utils/ofStatistics'
import type { ChartData } from 'chart.js'
import { format, parseISO } from 'date-fns'

import type { StatisticsData } from '@/modules/overview/types'
import { WEEK_DAYS } from '@/modules/overview/types'
import { formatAMPM } from '@/utils/helpers'

import type { DetailedStatisticsType } from './types'

export const mapIncomeSourcesData = (
  statistics: StatisticsData | undefined,
  isOneDay?: boolean,
) => {
  if (!statistics) return

  const res: ChartData<'line'> = {
    labels: Object.values(statistics.data).map((day) =>
      format(parseISO(day.key), isOneDay ? 'h aaa' : 'MMM dd, yy'),
    ),
    datasets: [
      {
        label: 'Subscriptions',
        data: Object.values(statistics.data).map(
          (day) => day.totalSubscriptionsRevenue,
        ),
        borderColor: '#EF358E',
        backgroundColor: '#EF358E',
      },
      {
        label: 'Messages',
        data: Object.values(statistics.data).map((day) => day.messageRevenue),
        borderColor: '#9601CA',
        backgroundColor: '#9601CA',
      },
      {
        label: 'Tips',
        data: Object.values(statistics.data).map((day) => day.tipsRevenue),
        borderColor: '#0BC9CD',
        backgroundColor: '#0BC9CD',
      },
    ],
  }

  return res
}

export const mapBestDayData = (statistics: StatisticsData | undefined) => {
  if (!statistics) return

  const labels = WEEK_DAYS.map(
    (day) => day[0] + day.substring(1, 3).toLowerCase(),
  )
  const data = WEEK_DAYS.map((w) => statistics.bestDay[w] ?? 0)

  const res: ChartData<'bar'> = {
    labels: labels,
    datasets: [
      {
        data: data,
        backgroundColor: '#1E84E3',
        borderRadius: 4,
        barThickness: 15,
      },
    ],
  }

  return res
}

export const mapBestHourData = (statistics: StatisticsData | undefined) => {
  if (!statistics) return

  const res: ChartData<'bar'> = {
    labels: Object.keys(statistics.bestHour).map((item) =>
      formatAMPM(Number(item)),
    ),
    datasets: [
      {
        data: Object.values(statistics.bestHour),
        backgroundColor: '#1E84E3',
        borderRadius: 4,
        maxBarThickness: 15,
      },
    ],
  }

  return res
}

export const mapDetailedStatisticsData = (
  statistics: StatisticsData | undefined,
  isOneDay = false,
  useAllSubsRevenue = false,
): DetailedStatisticsType[] => {
  if (!statistics) return []
  if (Object.entries(statistics.data).length <= 0) return []

  if (isOneDay) {
    const total = statistics.total
    const originalDate = new Date(Object.keys(statistics.data)[0])

    return [
      {
        key: Object.keys(statistics.data)[0],
        originalDate,
        date: format(originalDate, 'MMM dd, yyyy'),
        totalRevenue: total.totalRevenue,
        newSubs: total.newSubscriptions,
        newSubscriptionsRevenue: total.newSubscriptionsRevenue,
        recurringSubscriptionsRevenue: total.recurringSubscriptionsRevenue,
        totalSubscriptionsRevenue: total.totalSubscriptionsRevenue,
        tipsRevenue: total.tipsRevenue,
        averageFanSpend: total.averageFanSpend,
        spendingFansCount: total.spendingFansCount,
        messageRevenue: total.messageRevenue,
        textingRatio: calculateTextingRatio(
          useAllSubsRevenue
            ? total.totalSubscriptionsRevenue
            : total.newSubscriptionsRevenue,
          total.messageRevenue,
          total.tipsRevenue,
        ),
        openChats: total.openChatCount,
        sellingChats: total.sellingChatCount,
        linkClicks: total.linkClicks,
        conversionRate: calculateConversionRate(
          total.linkClicks,
          total.newSubscriptions,
        ),
      },
    ]
  }

  return Object.values(statistics.data).map((day) => {
    const originalDate = new Date(day.key)

    return {
      key: day.key,
      originalDate,
      date: format(new Date(day.key), 'MMM dd, yyyy'),
      totalRevenue: day.totalRevenue,
      newSubs: day.newSubscriptions,
      newSubscriptionsRevenue: day.newSubscriptionsRevenue,
      recurringSubscriptionsRevenue: day.recurringSubscriptionsRevenue,
      totalSubscriptionsRevenue: day.totalSubscriptionsRevenue,
      tipsRevenue: day.tipsRevenue,
      averageFanSpend: day.averageFanSpend,
      spendingFansCount: day.spendingFansCount,
      messageRevenue: day.messageRevenue,
      textingRatio: calculateTextingRatio(
        useAllSubsRevenue
          ? day.totalSubscriptionsRevenue
          : day.newSubscriptionsRevenue,
        day.messageRevenue,
        day.tipsRevenue,
      ),
      openChats: day.openChatCount,
      sellingChats: day.sellingChatCount,
      linkClicks: day.linkClicks,
      conversionRate: calculateConversionRate(
        day.linkClicks,
        day.newSubscriptions,
      ),
    }
  })
}
